import React from "react";
import {
  Card,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DateTime } from "luxon";
import { fpColors } from "../../../theme/fpTheme.ts";

interface Props {
  title: string;
  count?: number | string;
  countUnit?: string;
  value?: number | string;
  valueUnit?: string;
  children?: React.ReactNode;
  minWidth?: number;
  loading?: boolean; // Whether data is being loaded
  hasData?: boolean; // Whether we have data to display
  lastRefreshed?: number | null;
  onRefresh?: () => void;
}

const StyledWidgetCard = styled(Card)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  margin: "3px",
  padding: "12px",
  borderRadius: "10px",
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  position: "relative",
  transition: "all 0.3s ease-in-out",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
  display: "flex",
  flexDirection: "column",
  minHeight: "calc(100% - 6px)", // Min-height instead of fixed height
  "&:hover": {
    boxShadow: "0 6px 24px 0 rgba(0, 0, 0, 0.08)",
    borderColor: alpha(fpColors.fpgreen, 0.3),
  },
}));

const AccentBorder = styled(Box)(({ theme }) => ({
  width: "60px",
  height: "3px",
  background: fpColors.fpgreen,
  borderRadius: "3px",
}));

const WidgetCard: React.FC<Props> = ({
  title,
  count,
  countUnit,
  value,
  valueUnit,
  children,
  minWidth = 200,
  loading = false,
  hasData = false,
  lastRefreshed,
  onRefresh,
}) => {
  const hasCount = count !== undefined && count !== null && count !== "";
  const hasValue = value !== undefined && value !== null && value !== "";
  const showFooter = lastRefreshed || onRefresh;

  // Only show the main metrics area when there is something to display
  const showMain = (loading && !hasData) || hasCount || hasValue;

  const countPart = hasCount ? count : null;
  const valuePart = hasValue ? value : null;

  // Format timestamp to readable string using Luxon
  const formattedLastRefreshed = lastRefreshed
    ? DateTime.fromMillis(lastRefreshed).toLocaleString({
        ...DateTime.DATETIME_SHORT,
        hour12: true,
      }) +
      " " +
      DateTime.local().offsetNameShort
    : "";

  const formatNumber = (val: any) => {
    if (val === null || val === undefined) return val;

    const num = Number(val); // ✅ force convert string → number
    if (isNaN(num)) return val; // return as-is if not numeric

    // round safely
    const rounded = Math.round((num + Number.EPSILON) * 100) / 100;

    return Number.isInteger(rounded) ? rounded : rounded.toFixed(2);
  };

  return (
    <StyledWidgetCard sx={{ minWidth }}>
      {/* Header section */}
      <Box sx={{ mb: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: fpColors.fpdarkblue, fontWeight: 600 }}
        >
          {title}
        </Typography>
        <AccentBorder sx={{ mt: 0.5 }} />
      </Box>

      {/* Main content area - metrics or spinner (render only when needed) */}
      {showMain && (
        <Box
          sx={{
            mt: "3px",
            mb: "0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: "30px",
          }}
        >
          {loading && !hasData ? (
            <CircularProgress size={24} sx={{ color: fpColors.fpgreen }} />
          ) : (
            <>
              {countPart !== null && (
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{ color: fpColors.fpdarkblue }}
                >
                  {formatNumber(countPart)} {countUnit ?? ""}
                </Typography>
              )}

              {hasCount && hasValue && (
                <Typography
                  variant="h5"
                  sx={{ mx: 1, color: fpColors.fpgrey, opacity: 0.5 }}
                >
                  |
                </Typography>
              )}

              {valuePart !== null && (
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{ color: fpColors.fplightblue }}
                >
                  {formatNumber(valuePart)} {valueUnit ?? ""}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* Widget content - using flex-grow to take available space */}
      {children && <Box sx={{ mt: 2, flexGrow: 1 }}>{children}</Box>}

      {/* Footer with timestamp and refresh button/spinner - ensure it's always visible */}
      {showFooter && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: "auto", // Push to bottom when space available
            pt: 0.5,
            borderTop: `1px solid rgba(0, 0, 0, 0.06)`,
            opacity: 0.8,
            position: "relative", // Ensure it's in the normal flow
            minHeight: "10px", // Minimum height to ensure visibility
          }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: fpColors.fpgreen }} />
          ) : (
            <>
              {lastRefreshed && (
                <Typography
                  variant="caption"
                  noWrap // Prevent text wrapping
                  sx={{
                    color: fpColors.fpgrey,
                    mr: 1,
                    maxWidth: "70%", // Limit width to prevent layout issues
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Updated: {formattedLastRefreshed}
                </Typography>
              )}
              {onRefresh && (
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  disabled={loading}
                  sx={{
                    p: 0.5,
                    color: fpColors.fpgreen,
                    ml: "auto", // Push to right
                    "&:hover": {
                      backgroundColor: alpha(fpColors.fpgreen, 0.1),
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      )}
    </StyledWidgetCard>
  );
};

export default WidgetCard;
